import React, { useEffect, useRef, useState } from 'react';
import { Loader2, FileText } from 'lucide-react';
import pdfjsLib from '../utils/pdfjs';

const MAX_PAGES = 20;

/**
 * Renders a PDF's pages as canvases, fitted to the container width.
 * Used on mobile, where <object type="application/pdf"> doesn't render (iOS).
 * source: URL string or Blob/File.
 */
function PdfPages({ source }) {
  const containerRef = useRef(null);
  const [state, setState] = useState('loading'); // loading | done | error
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let pdf = null;
    const container = containerRef.current;
    if (!source || !container) return undefined;
    container.innerHTML = '';
    setState('loading');

    (async () => {
      try {
        const data = typeof source === 'string'
          ? { url: source }
          : { data: new Uint8Array(await source.arrayBuffer()) };
        pdf = await pdfjsLib.getDocument(data).promise;
        if (cancelled) return;
        setPageCount(pdf.numPages);

        const width = container.clientWidth || window.innerWidth;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        for (let n = 1; n <= Math.min(pdf.numPages, MAX_PAGES); n++) {
          const page = await pdf.getPage(n);
          if (cancelled) return;
          const viewport = page.getViewport({ scale: (width / page.getViewport({ scale: 1 }).width) * dpr });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = '100%';
          canvas.className = 'bg-white shadow rounded';
          container.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          if (n === 1 && !cancelled) setState('done');
        }
      } catch (err) {
        console.error('Error rendering PDF:', err);
        if (!cancelled) setState('error');
      }
    })();

    return () => {
      cancelled = true;
      pdf?.destroy();
    };
  }, [source]);

  return (
    <div className="w-full">
      {state === 'loading' && (
        <div className="py-16 flex justify-center">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      )}
      {state === 'error' && (
        <div className="py-16 flex flex-col items-center text-gray-500">
          <FileText className="w-12 h-12 text-red-500 mb-2" />
          <p className="text-sm">Can't display this PDF here — use Open below.</p>
        </div>
      )}
      <div ref={containerRef} className="flex flex-col gap-3" />
      {pageCount > MAX_PAGES && (
        <p className="py-3 text-center text-sm text-gray-500">Showing the first {MAX_PAGES} of {pageCount} pages</p>
      )}
    </div>
  );
}

export default PdfPages;
